import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Loader2, Volume2, VolumeX } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useProgression } from '@/hooks/useProgression';
import { useSiteBehavior } from '@/hooks/useSiteBehavior';
import { useAIMemory } from '@/hooks/useAIMemory';
import { useVoiceMode } from '@/hooks/useVoiceMode';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const AIGuide = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasGreeted, setHasGreeted] = useState(false);
  const [hesitationTime, setHesitationTime] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { state } = useProgression();
  const { behavior, trackSilence } = useSiteBehavior();
  const { recordConversation, getMemoryContext, hasMemory, getDaysSinceFirst } = useAIMemory();
  const { isSupported: voiceSupported, isEnabled: voiceEnabled, toggleEnabled: toggleVoice, speak, isSpeaking, stop: stopSpeaking } = useVoiceMode();

  // Track hesitation when chat is open
  useEffect(() => {
    if (!isOpen) {
      setHesitationTime(0);
      return;
    }
    
    const interval = setInterval(() => {
      setHesitationTime(prev => {
        const newTime = prev + 1;
        if (newTime % 30 === 0) {
          trackSilence(30);
        }
        return newTime;
      });
    }, 1000);
    
    return () => clearInterval(interval);
  }, [isOpen, trackSilence]);

  useEffect(() => {
    if (isOpen && !hasGreeted && messages.length === 0) {
      // Memory-aware + behavior-aware greeting
      let greeting = 'You have found the guide. What draws you here?';
      
      const daysSince = getDaysSinceFirst();
      
      if (hasMemory && daysSince > 7) {
        greeting = `It has been ${daysSince} days since we first spoke. What brings you back?`;
      } else if (hasMemory && daysSince > 0) {
        greeting = 'You returned. The rooms remember you. What draws you here?';
      } else if (behavior.rushBehavior > 70) {
        greeting = 'You move quickly. Perhaps too quickly. What are you running from?';
      } else if (behavior.patientBehavior > 70) {
        greeting = 'You take your time. The rooms notice. What brings you to the guide?';
      } else if (behavior.silenceTime > 300) {
        greeting = 'You sit in silence often. I see that. What would you ask?';
      } else if (behavior.earlyExitCount > 5) {
        greeting = 'You leave rooms before finishing them. What are you avoiding?';
      } else if (behavior.visitsCount > 20) {
        greeting = 'You return here often. What keeps drawing you back?';
      }
      
      setMessages([{ role: 'assistant', content: greeting }]);
      setHasGreeted(true);
    }
  }, [isOpen, hasGreeted, messages.length, behavior, hasMemory, getDaysSinceFirst]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const journeyStats = {
        roomsVisited: state.completedRooms.length,
        completedRooms: state.completedRooms.length,
        patterns: state.completedRooms.slice(-5).join(', ') || 'none yet'
      };
      
      const behaviorPatterns = {
        rushBehavior: behavior.rushBehavior,
        patientBehavior: behavior.patientBehavior,
        silenceTime: behavior.silenceTime,
        earlyExitCount: behavior.earlyExitCount,
        visitsCount: behavior.visitsCount,
        hesitationTime
      };
      
      // Get memory context for cross-session awareness
      const memoryContext = getMemoryContext();

      const { data, error } = await supabase.functions.invoke('ai-guide', {
        body: {
          messages: [...messages, { role: 'user', content: userMessage }].map(m => ({
            role: m.role,
            content: m.content
          })),
          journeyStats,
          behaviorPatterns,
          memoryContext
        }
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      const assistantMessage = data.message;
      setMessages(prev => [...prev, { role: 'assistant', content: assistantMessage }]);
      
      // Speak the response if voice is enabled
      if (voiceEnabled && assistantMessage) {
        speak(assistantMessage);
      }
      
      // Record conversation to memory
      const userMessages = messages.filter(m => m.role === 'user').map(m => m.content);
      userMessages.push(userMessage);
      recordConversation(userMessages);
    } catch (err) {
      console.error('Guide error:', err);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'The guide is momentarily silent. Try again.' 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Toggle Button - larger on mobile for better touch target */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 w-14 h-14 sm:w-12 sm:h-12 flex items-center justify-center border rounded-full sm:rounded-none transition-all duration-500 touch-feedback ${
          isOpen 
            ? 'bg-foreground/10 border-foreground/30 rotate-0' 
            : 'bg-card/80 border-border/30 hover:border-foreground/20'
        }`}
        aria-label={isOpen ? 'Close guide' : 'Open guide'}
      >
        {isOpen ? (
          <X className="w-5 h-5 sm:w-4 sm:h-4 text-foreground/60" />
        ) : (
          <MessageCircle className="w-5 h-5 sm:w-4 sm:h-4 text-muted-foreground/50" />
        )}
      </button>

      {/* Chat Panel - responsive sizing */}
      {isOpen && (
        <div className="fixed inset-4 sm:inset-auto sm:bottom-20 sm:right-6 z-40 sm:w-80 sm:h-96 bg-card/98 sm:bg-card/95 border border-border/30 flex flex-col animate-fade-in backdrop-blur-md sm:backdrop-blur-sm rounded-lg sm:rounded-none">
          {/* Header */}
          <div className="p-4 border-b border-border/20 flex items-center justify-between">
            <p className="text-xs text-muted-foreground/50 uppercase tracking-wider">
              the guide
            </p>
            <div className="flex items-center gap-2">
              {/* Voice Mode Toggle */}
              {voiceSupported && (
                <button
                  onClick={() => {
                    if (isSpeaking) stopSpeaking();
                    toggleVoice();
                  }}
                  className={`p-2 transition-colors ${voiceEnabled ? 'text-primary' : 'text-muted-foreground/40 hover:text-muted-foreground/60'}`}
                  aria-label={voiceEnabled ? 'Disable voice' : 'Enable voice'}
                >
                  {voiceEnabled ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
                </button>
              )}
              {/* Mobile close button in header */}
              <button 
                onClick={() => setIsOpen(false)}
                className="sm:hidden p-2 -mr-2 text-muted-foreground/50"
                aria-label="Close guide"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`${
                  msg.role === 'user' 
                    ? 'text-right' 
                    : 'text-left'
                }`}
              >
                <p className={`inline-block max-w-[85%] px-3 py-2.5 sm:py-2 text-sm sm:text-sm rounded-lg sm:rounded-none ${
                  msg.role === 'user'
                    ? 'bg-foreground/10 text-foreground/80'
                    : 'text-muted-foreground/70 italic'
                }`}>
                  {msg.content}
                </p>
              </div>
            ))}
            
            {isLoading && (
              <div className="flex items-center gap-2 text-muted-foreground/40">
                <Loader2 className="w-3 h-3 animate-spin" />
                <span className="text-xs">...</span>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input - larger touch targets on mobile */}
          <div className="p-3 sm:p-3 border-t border-border/20">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="ask the guide..."
                className="flex-1 bg-transparent border border-border/20 px-4 py-3 sm:py-2 text-base sm:text-sm rounded-lg sm:rounded-none placeholder:text-muted-foreground/20 focus:outline-none focus:border-foreground/20 transition-colors"
                disabled={isLoading}
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || isLoading}
                className={`p-3 sm:p-2 rounded-lg sm:rounded-none transition-colors min-w-[48px] min-h-[48px] sm:min-w-0 sm:min-h-0 flex items-center justify-center ${
                  input.trim() && !isLoading
                    ? 'text-foreground/60 hover:text-foreground bg-foreground/5 sm:bg-transparent'
                    : 'text-muted-foreground/20'
                }`}
              >
                <Send className="w-5 h-5 sm:w-4 sm:h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIGuide;
